import React from 'react';
import { Lock, FileCheck2, UserMinus, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader.js';
import { Card } from '../../components/Card.js';

export const Solutions: React.FC = () => {
  const solutions = [
    {
      icon: <Lock className="w-7 h-7 text-emerald-600" />,
      number: '01',
      title: '管理者でも絶対に消せない「隔離金庫」',
      subtitle: '外部クラウドの強力ロック機能で、データを物理的に保護',
      description:
        '保存されたデータは、Googleとは別の強固な金庫に保管され、一定期間（1年〜無期限）は、会社の社長やシステム管理者であっても削除・変更することが物理的にできません。万が一アカウントを乗っ取られても、データは100%守られます。',
      benefits: [
        '管理者アカウント乗っ取り・ウイルス感染時もデータは完全無傷',
        '最高強度の暗号化で、外部からの盗聴や漏洩を徹底防止',
        '誤操作によるファイル上書き・誤削除も即座に過去バージョンへ復旧',
      ],
      tag: '削除不可ロック',
      badgeBg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    },
    {
      icon: <FileCheck2 className="w-7 h-7 text-sky-600" />,
      number: '02',
      title: '改ざんされていないことを客観証明する「原本証明書」',
      subtitle: '最新の暗号技術で「過去のデータの真正性」を立証',
      description:
        'バックアップされたファイルやメールには、それぞれデジタルな電子指紋を記録します。これにより、後から「メールが書き換えられたのではないか」と疑われた場合でも、「指定した日時に確かに存在し、1文字も改ざんされていない原本であること」を客観的に証明できます。',
      benefits: [
        '社内不正・労務トラブル時の強力な客観的証拠になる',
        'バックアップデータが壊れていないかを画面上でワンクリック検証',
        '誰がいつ保存したか、過去の全履歴を正確に追跡',
      ],
      tag: '改ざん防止・原本証明',
      badgeBg: 'bg-sky-50 text-sky-800 border-sky-200',
    },
    {
      icon: <UserMinus className="w-7 h-7 text-teal-600" />,
      number: '03',
      title: '退職者のGoogleアカウントを安全に解約し、コスト削減',
      subtitle: '全データを自動保存した上でアカウントを停止、無駄な月額費用をゼロに',
      description:
        '退職する社員のGoogleアカウントを指定するだけで、メール・ドライブ・カレンダー・連絡先をまるごと自動バックアップ。安全に保管されたことを確認してからアカウントを解約できるため、過去のファイル喪失リスクなく、毎月のライセンス費用を大幅に削減できます。',
      benefits: [
        '退職者のアカウントを即日解約し、無駄な月額課金をストップ',
        '必要な過去のメールやファイルは、管理画面からいつでも検索・取り出し可能',
        '退職者ごとのバックアップ完了証明レポートを自動発行',
      ],
      tag: 'コスト削減',
      badgeBg: 'bg-teal-50 text-teal-800 border-teal-200',
    },
    {
      icon: <ShieldCheck className="w-7 h-7 text-indigo-600" />,
      number: '04',
      title: '自社サーバーの構築不要、安心・安全なクラウド連携',
      subtitle: 'パスワードや秘密鍵の管理が不要な最新クラウドセキュリティ',
      description:
        '社内に高価なバックアップ専用サーバーを立てる必要はありません。Googleの安全なAPIと公式認証基盤を通じて連携するため、パスワードやアクセスキーの漏洩リスクがなく、最短即日で安全に稼働を開始できます。',
      benefits: [
        'パスワードや秘密鍵ファイルの流出リスクがゼロ',
        'バックアップソフトは「閲覧のみ」の権限で動作するため、元のデータが書き換わる心配なし',
        '自社専用の独立したクラウド領域で稼働するため、他社からの影響なし',
      ],
      tag: 'サーバー不要・即日連携',
      badgeBg: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    },
  ];

  return (
    <section id="solution" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          tag="ARAYAの4つの強み"
          tagVariant="emerald"
          title="貴社の大切なデータを守り抜く4つの解決策"
          description="ARAYA（阿頼耶）は、Google Workspaceとは完全に切り離された「安全な金庫」にデータを自動保存。管理者でも絶対に消せない仕組みと、退職者コストの削減を同時に実現します。"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((item, index) => (
            <Card key={index} glow={index === 0 ? 'emerald' : index === 1 ? 'cyan' : 'none'} className="p-8 sm:p-10 flex flex-col justify-between bg-white border-slate-200 shadow-sm hover:shadow-lg">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3.5">
                    <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 shadow-xs">
                      {item.icon}
                    </div>
                    <div>
                      <span className="text-xs font-mono font-bold text-slate-400">PILLAR {item.number}</span>
                      <p className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-full border mt-0.5 ${item.badgeBg}`}>
                        {item.tag}
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-1.5 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-xs font-mono text-slate-500 mb-5">{item.subtitle}</p>

                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  {item.description}
                </p>

                <div className="space-y-2.5 mb-6">
                  {item.benefits.map((b, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-xs text-slate-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="font-mono">ADR Architecture Standard</span>
                <span className="text-emerald-700 font-semibold flex items-center gap-1 hover:text-emerald-600 transition-colors">
                  エンタープライズ準拠 <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
